import { useMemo } from 'react';

import {
  OperationFields,
  OperationStates,
  OperationTypes,
  FetchOperationsCacheKey
} from '../../../../../operationsTypes';
import { NoTATFormData } from '../../NoTATForm.types';
import { UserID } from '../../../../../../users/usersTypes';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';
import { CREATE_OPERATION_QUERY } from '../../../../../queries/createOperation.query';

import { useCreateOperation } from '../../../../../hooks/useCreateOperation';

import { dateFnsConvert } from '../../../../../../../utils/dateFnsConvert';

import { createOperationFormSchema } from './useNoTATForm.schema';
import { T_A_T_SUBJECT } from '../../../../../operationsConstants';

interface useNoTATFormOptions {
  cacheKeys?: FetchOperationsCacheKey[];
  clientId: UserID;
  onSuccess?: () => void;
}

function useNoTATForm({ cacheKeys, onSuccess, clientId }: useNoTATFormOptions) {
  const defaultValues = useMemo<NoTATFormData>(
    () => ({
      clientId,
      subject: T_A_T_SUBJECT,
      body: null,
      date: null,
      completeDatetime: null,
      state: OperationStates.COMPLETED,
      operationType: OperationTypes.TASK,
      mentionedUserIds: []
    }),
    [clientId]
  );

  const { control, errors, handleSubmitReactHookForm, register, resetForm } =
    useReactHookForm<NoTATFormData>({
      defaultValues,
      isModalForm: true,
      schema: createOperationFormSchema
    });

  const {
    createOperation,
    createOperationErrorMessage,
    createOperationReset,
    createOperationLoading
  } = useCreateOperation({
    query: CREATE_OPERATION_QUERY,
    cacheKeys
  });

  return {
    validationErrors: {
      subjectValidationError: errors?.subject?.message,
      bodyValidationError: errors?.body?.message,
      dateValidationError: errors?.date?.message,
      completeDatetimeValidationError: errors?.completeDatetime?.message,
      clientIdValidationError: errors?.clientId?.message
    },
    control,
    resetNoTATForm: resetForm,
    createOperationReset,
    createOperationErrorMessage,
    createOperationLoading,
    handleLeaveOfAbsence: handleSubmitReactHookForm({
      onSubmit: async (data) =>
        createOperation({
          ...data,
          completeDatetime: dateFnsConvert.toDateTimeWithTimezone(
            data.completeDatetime
          ),
          date: dateFnsConvert.toDateTimeWithTimezone(data.date)
        }).then(() => {
          onSuccess?.();
        }),
      dirtyFieldsOnly: false
    }),

    registerFields: {
      registerBody: register(OperationFields.BODY),
      registerDate: register(OperationFields.DATE),
      registerCompleteDatetime: register(OperationFields.COMPLETE_DATETIME),
      registerSubject: register(OperationFields.SUBJECT)
    }
  };
}

export default useNoTATForm;
